import React, { useState, useEffect } from 'react';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { globalCss as global } from '@uhg-abyss/web/tools/styled';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import { SideNav } from '../Documentation/SideNav';
import { PageHeading } from '../Documentation/PageHeading';
import { ServiceSandboxV2 } from '@src/common/ServiceSandboxV2';
import { APIDetails } from '../Documentation/APIDetails/APIDetails';
import { ErrorCodesV2 } from './ErrorCodesV2';
import { useDocumentation } from '@src/hooks/useDocumentation';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { event } from '@uhg-abyss/web/tools/event';
import { useSelector } from 'react-redux';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';

const UpdatedSandboxStyle = global({
  '.abyss-table-cell': {
    wordBreak: 'unset !important',
  },
  '.abyss-box-root': {
    alignItems: 'flex-start',
  },
});

const StyledContent = (props) => {
  return (
    <Grid.Col
      css={{
        'abyss-grid-col': {
          paddingLeft: '0px !important',
          paddingRight: '0px !important',
        },
      }}
      {...props}
    />
  );
};

const INITIAL_CURRENT_API = {
  description: '',
  isDisabled: false,
  method: '',
  operationId: '',
  operationSlug: '',
  parameters: [],
  path: '',
  produces: [],
  responses: [],
  summary: '',
  tags: [],
  schema: {},
};

export const DocumentationV2 = () => {
  const service = 'superconnector';

  const [currentApi, setCurrentApi] = useState(INITIAL_CURRENT_API);

  const { navigate } = useRouter();
  const { slug } = useParams();

  const dsUserInfo = useSelector((state) => {
    return state.GET_DS_USER_INFO.data;
  });
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });

  const handleTabChange = (tab) => {
    const interactionName = `${service} ${currentApi.method} ${currentApi.summary}`;
    const interactionValueList = ['api sandbox', 'api details', 'error codes'];
    const interactionValue = interactionValueList[tab];
    const interactionContext = 'tab clicks';
    event('INTERACTION_EVENT', {
      interactionName,
      interactionValue,
      interactionContext,
    });
  };

  const getCurrentApi = (data, slugParm) => {
    let returnVal;
    data.pathsByTag.forEach((listByTag) => {
      listByTag.paths.forEach((path) => {
        if (path.operationSlug.toLowerCase() === slugParm?.toLowerCase()) {
          returnVal = path;
        }
      });
    });

    if (returnVal) {
      setCurrentApi({ ...returnVal, schema: data?.components?.schemas || {} });
      if (slugParm) {
        event('DOCUMENTATION_PAGE_LOAD', {
          service,
          slug: slugParm,
          dsUserInfo,
          userInfo,
        });
      }
    }
  };

  useEffect(() => {
    getCurrentApi(data, slug);
  }, [slug]);

  const handleCompleted = React.useCallback(
    (data) => {
      if (data.pathsByTag && data.pathsByTag[0]) {
        const returnVal = { ...data.pathsByTag[0].paths[0] };
        navigate(
          `/documentation/${data.service}/${returnVal.operationSlug || slug}`
        );
        getCurrentApi(data, returnVal.operationSlug || slug);
      }
    },
    [slug, service]
  );

  const [documentation, getDocumentation] = useDocumentation({
    onCompleted: handleCompleted,
    onError: () => {
      setCurrentApi(INITIAL_CURRENT_API);
    },
  });

  const { data } = documentation;

  useEffect(() => {
    if (service) {
      // Reset state to initial values
      setCurrentApi(INITIAL_CURRENT_API);

      // Fetch new documentation data
      getDocumentation({ userInfo, service });
    }
  }, []);

  return (
    <LoadingOverlay
      label="superconnector"
      isLoading={documentation.loading}
      isError={documentation.error}
    >
      <Grid>
        <StyledContent span={3}>
          <SideNav>
            <SideNav.Link
              service={service}
              data={data}
              currentTags={currentApi.tags}
            />
          </SideNav>
        </StyledContent>
        <StyledContent span={9}>
          <div>
            <PageHeading
              info={data?.info}
              currentApi={currentApi}
              service={service}
            />
            <Tabs
              onTabChange={handleTabChange}
              css={{
                '.abyss-tabs-tab-content': { fontSize: '15px' },
              }}
            >
              <Tabs.Tab label="API Sandbox">
                <UpdatedSandboxStyle />
                <ServiceSandboxV2 currentApi={currentApi} swagger={data} />
              </Tabs.Tab>
              <Tabs.Tab label="API Details">
                <APIDetails currentApi={currentApi} />
              </Tabs.Tab>
              <Tabs.Tab label="Error Codes">
                <ErrorCodesV2 currentApi={currentApi} />
              </Tabs.Tab>
            </Tabs>
          </div>
        </StyledContent>
      </Grid>
    </LoadingOverlay>
  );
};
